import React, {useEffect, useState} from 'react'
import {useSelector} from 'react-redux';
import {getInvoiceTDS, getBeneficiaryPPATrail} from '../../../helper/Api';
import DataTable from 'react-data-table-component';
import {AddCommasToAmount} from "../../../helper/Utils";
import {useLocation} from "react-router-dom";
import moment from 'moment';
import { Badge } from 'react-bootstrap';
import { Modal, Box, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { AiOutlineVerticalAlignBottom, AiOutlineEye } from 'react-icons/ai';
import './OtherList.css';

const MultiLineHeader = ({ lines, align = 'center' }) => (
    <div className={`other-col-header other-col-header--${align}`}>
        {lines.map((line, idx) => (
            <React.Fragment key={idx}>
                {idx > 0 && <br />}
                {line}
            </React.Fragment>
        ))}
    </div>
);

const CenterCell = ({ children }) => (
    <div className="other-cell-center">{children}</div>
);

const isOfflineRow = (row) => String(row?.payment_mode || '').toLowerCase() === 'offline';
const isOnlineRow = (row) => String(row?.payment_mode || '').toLowerCase() === 'online';

const getPaymentStatus = (payment_mode, flag_success_return) => {
    if (payment_mode === 'online') {
        if (flag_success_return === '1') {
            return { label: 'Success', color: '#d4edda', textColor: '#155724' };
        }
        if (flag_success_return === '0') {
            return { label: 'Rejected', color: '#f8d7da', textColor: '#721c24' };
        }
        if (flag_success_return === null) {
            return { label: 'Pending', color: '#fff3cd', textColor: '#856404' };
        }
        if (flag_success_return === '2') {
            return { label: 'Failed', color: '#f8d7da', textColor: '#721c24' };
        }
        if (flag_success_return === '3') {
            return { label: 'On Hold', color: '#f8d7da', textColor: '#856404' };
        }
        return { label: 'Sent To Bank', color: '#fff3cd', textColor: '#856404' };
    }
    return { label: 'Success', color: '#d4edda', textColor: '#155724' };
};

const actionIconBtnStyle = {
    width: 28,
    height: 28,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    background: 'transparent',
};

const OTHERList = ({setSelectednvoice, fromDate, toDate, searchStr, dateColumn, loading, setLoading}) => {
    const {authData} = useSelector((state) => state.authData);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    let {state} = useLocation();
    const [data, setData] = useState(state && state.data ? state.data : []);
    const [openModal, setOpenModal] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState([]);

    const fetchChallans = async () => {
        try {
            setLoading(true);
            const data = await getInvoiceTDS({
                from_date: fromDate,
                to_date: toDate,
                searchStr: searchStr,
                dateColumn: dateColumn,
                other: true,
            });
            if (data.data.status) {
                setData(data.data.list);
            }
        } catch (error) {
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchChallans();
    }, [fromDate, toDate, searchStr, dateColumn]);

    const handleDownload = (url) => {
        if (!url) return;
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const fetchPaymentTrail = async (ppa_invoice_batch_id, account_no) => {
        try {
            setLoading(true);
            const response = await getBeneficiaryPPATrail({ ppa_invoice_batch_id, account_no });
            if (response?.data) {
                setSelectedRowData(response.data.result || []);
            }
        } catch (error) {
            setSelectedRowData([]);
            toast.error(error?.response?.data?.message || error?.message || 'Failed to fetch payment trail', {
                position: toast.POSITION.TOP_CENTER,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = async (rowData) => {
        await fetchPaymentTrail(
            rowData.ppa_invoice_batch_id,
            rowData.reimbursement_beneficiary_account_no || rowData.account_no
        );
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedRowData([]);
    };

    const customStyles = {
        table: {
            style: {
                width: '100%',
            },
        },
        headRow: {
            style: {
                minHeight: '52px',
            },
        },
        headCells: {
            style: {
                fontWeight: 'bold',
                fontSize: '14px',
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'clip',
                wordBreak: 'break-word',
                lineHeight: 1.35,
                paddingTop: '8px',
                paddingBottom: '8px',
            },
        },
    };

    const columns = [
        {
            name: 'Actions',
            selector: (row) => (
                <div className="d-flex align-items-center justify-content-center" style={{ gap: '6px', width: '100%' }}>
                    <button
                        type="button"
                        title="Download Attachment"
                        disabled={!row?.invoice}
                        onClick={() => handleDownload(row.invoice)}
                        style={{
                            ...actionIconBtnStyle,
                            border: '1px solid #1976d2',
                            color: '#1976d2',
                            cursor: row?.invoice ? 'pointer' : 'not-allowed',
                            opacity: row?.invoice ? 1 : 0.5,
                        }}
                    >
                        <AiOutlineVerticalAlignBottom size={16} />
                    </button>
                    {isOnlineRow(row) && (
                        <button
                            type="button"
                            title="View Payment Trail"
                            onClick={() => handleOpenModal(row)}
                            style={{
                                ...actionIconBtnStyle,
                                border: '1px solid #f59e0b',
                                color: '#f59e0b',
                                cursor: 'pointer',
                            }}
                        >
                            <AiOutlineEye size={16} />
                        </button>
                    )}
                </div>
            ),
            width: '110px',
            minWidth: '110px',
            center: true,
        },
        {
            name: <MultiLineHeader lines={['Beneficiary', 'Details']} align="left" />,
            selector: (row) => (
                <div>
                    <div>{row.company_name}</div>
                    <div>
                        <b>Pan No.:</b> {row.pan_no}
                    </div>
                    <Badge bg={row?.payment_mode === 'offline' ? 'secondary' : 'primary'} className="mb-1">
                        {row?.payment_mode?.toUpperCase()}
                    </Badge>
                </div>
            ),
            sortable: true,
            wrap: true,
            minWidth: '240px',
            width: '240px',
        },
        {
            name: <MultiLineHeader lines={['Invoice', 'Details']} />,
            selector: (row) => (
                <CenterCell>
                    <div>{row.invoice_no}</div>
                    <Badge bg="warning" className="mt-1">
                        {moment(row.invoice_date, 'YYYY-MM-DD').format('DD-MM-YYYY')}
                    </Badge>
                </CenterCell>
            ),
            center: true,
            minWidth: '175px',
            width: '175px',
        },
        {
            name: <MultiLineHeader lines={['Voucher', 'Details']} />,
            selector: (row) => (
                <CenterCell>
                    <div>{row.voucher_no}</div>
                    <Badge bg="primary" className="mt-1">
                        {moment(row.voucher_date, 'YYYY-MM-DD').format('DD-MM-YYYY')}
                    </Badge>
                </CenterCell>
            ),
            center: true,
            minWidth: '220px',
            width: '220px',
        },
        {
            name: <MultiLineHeader lines={['Taxable', 'Amount']} />,
            selector: (row) => (
                <CenterCell>
                    {row.taxable_amount ? AddCommasToAmount(row.taxable_amount) : ''}
                </CenterCell>
            ),
            center: true,
            minWidth: '150px',
            width: '150px',
        },
        {
            name: <MultiLineHeader lines={['Sanctioned', 'Amount']} />,
            selector: (row) => (
                <CenterCell>
                    {row.sanction_amount ? AddCommasToAmount(row.sanction_amount) : ''}
                </CenterCell>
            ),
            center: true,
            minWidth: '160px',
            width: '160px',
        },
        {
            name: <MultiLineHeader lines={['Other', 'Deduction']} />,
            selector: (row) => (
                <CenterCell>
                    {row.other_deduction ? AddCommasToAmount(row.other_deduction) : ''}
                </CenterCell>
            ),
            center: true,
            minWidth: '150px',
            width: '150px',
        },
        {
            name: <MultiLineHeader lines={['Challan', 'Details']} />,
            selector: () => <CenterCell>-</CenterCell>,
            center: true,
            minWidth: '180px',
            width: '180px',
        },
        {
            name: '',
            selector: () => '',
            grow: 1,
            minWidth: '1px',
            sortable: false,
            reorder: false,
        },
    ];

    const handleChangeRowsPerPage = (currentRowsPerPage) => {
        setRowsPerPage(currentRowsPerPage);
        setCurrentPage(1);
    };

    const handleSelectedChange = ({selectedRows}) => {
        setSelectednvoice(selectedRows.filter(isOfflineRow));
    };

    return (
        <div className="other-datatable-root">
            <Modal
                open={openModal}
                onClose={handleCloseModal}
                aria-labelledby="payment-trail-modal"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: 24,
                        padding: '24px',
                        maxWidth: '900px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflowY: 'auto',
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h2 id="payment-trail-modal" style={{ margin: 0 }}>Payment Trail Details</h2>
                        <Button onClick={handleCloseModal} sx={{ minWidth: 'auto', padding: 0 }}>
                            <CloseIcon />
                        </Button>
                    </Box>

                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #ddd', background: '#000', color: '#fff' }}>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Transaction ID</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Date</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Amount</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Status</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Remarks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedRowData.length > 0 ? (
                                selectedRowData.map((trail, index) => (
                                    <tr
                                        key={`trail_${trail.transaction_reference || 'notxn'}_${trail.created_at || 'nodate'}_${index}`}
                                        style={{ borderBottom: '1px solid #ddd' }}
                                    >
                                        <td style={{ padding: '12px' }}>{trail.transaction_reference || 'N/A'}</td>
                                        <td style={{ padding: '12px' }}>{trail.created_at || 'N/A'}</td>
                                        <td style={{ padding: '12px' }}>{trail.amount_paise || 'N/A'}</td>
                                        {index === 0 ? (
                                            <td style={{ padding: '12px' }}>
                                                {getPaymentStatus('online', trail.flag_success_return) ? (
                                                    <span
                                                        style={{
                                                            padding: '4px 8px',
                                                            borderRadius: '4px',
                                                            backgroundColor: getPaymentStatus('online', '').color,
                                                            color: getPaymentStatus('online', '').textColor,
                                                        }}
                                                    >
                                                        {getPaymentStatus('online', '').label}
                                                    </span>
                                                ) : (
                                                    'N/A'
                                                )}
                                            </td>
                                        ) : (
                                            <td style={{ padding: '12px' }}>
                                                {getPaymentStatus('online', trail.flag_success_return) ? (
                                                    <span
                                                        style={{
                                                            padding: '4px 8px',
                                                            borderRadius: '4px',
                                                            backgroundColor: getPaymentStatus('online', trail.flag_success_return).color,
                                                            color: getPaymentStatus('online', trail.flag_success_return).textColor,
                                                        }}
                                                    >
                                                        {getPaymentStatus('online', trail.flag_success_return).label}
                                                    </span>
                                                ) : (
                                                    'N/A'
                                                )}
                                            </td>
                                        )}
                                        <td style={{ padding: '12px' }}>
                                            {index === 0 ? 'N/A' : trail.reason || 'N/A'}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                                        No payment trail data available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    <Box sx={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <Button variant="contained" onClick={handleCloseModal}>
                            Close
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <DataTable
                customStyles={customStyles}
                className="dataTables_wrapper"
                selectableRows={authData.user.user_type === 'l1'}
                selectableRowDisabled={(row) => !isOfflineRow(row)}
                onSelectedRowsChange={handleSelectedChange}
                columns={columns}
                data={data}
                fixedHeader
                fixedHeaderScrollHeight="600px"
                pagination
                paginationPerPage={rowsPerPage}
                paginationTotalRows={data.length}
                paginationRowsPerPageOptions={[100, 200, 500, 1000]}
                onChangeRowsPerPage={handleChangeRowsPerPage}
            />
        </div>
    );
};

export default OTHERList;
